import { create } from 'zustand';
import { findFlowNodeItem } from '../components/flow/flowNodeCatalog';

const NODE_LABELS = {
  start: 'Start',
  end: 'End',
  message: 'Message',
  condition: 'Condition',
  input: 'Input',
  api_call: 'API Call',
};

const NODE_DESCRIPTIONS = {
  start: 'Entry point for the journey.',
  end: 'Finish the journey and close the flow.',
  message: 'Send a chat response to the user.',
  condition: 'Branch the journey with rules.',
  input: 'Capture a user response or variable.',
  api_call: 'Call an external API and store the result.',
};

const NODE_DEFAULT_CONFIG = {
  start: {
    label: 'Start',
  },
  end: {
    label: 'End',
    message: '',
  },
  message: {
    label: 'Welcome message',
    message: 'Hi there, how can I help you today?',
    mediaUrl: '',
    mediaType: 'text',
    alt: '',
  },
  condition: {
    label: 'Check condition',
    variable: 'customer_status',
    operator: 'equals',
    value: 'active',
  },
  input: {
    label: 'Ask input',
    prompt: 'What would you like to do next?',
    variable: 'user_reply',
    placeholder: 'Type your answer',
    required: true,
  },
  api_call: {
    label: 'API Call',
    asyncRequest: {
      method: 'POST',
      url: '',
      headers: {},
      body: {},
      query: {},
      pathParams: {},
      auth: { type: 'none' },
      timeout: 10000,
      retries: 2,
      retryDelayMs: 300,
      responseVariable: 'api_result',
    },
    responseMessage: '',
    responseMapping: [],
    variableAssignments: [],
  },
};

const API_TO_UI_TYPE = {
  start: 'start',
  end: 'end',
  text_message: 'message',
  question: 'input',
  condition: 'condition',
  api_call: 'api_call',
  quick_reply: 'input',
  list_message: 'message',
  genai_prompt: 'message',
  custom_function: 'api_call',
  trigger: 'message',
  jump_to_flow: 'message',
};

const isBrowser = typeof window !== 'undefined';

/** Purpose: map incoming variable scopes to supported editor values. Why: legacy `user` scope and unknown API values must not break flow editing. */
const normalizeVariableScope = (scope = '') => {
  const normalized = String(scope || '').trim().toLowerCase();
  if (normalized === 'global') return 'global';
  if (normalized === 'system') return 'system';
  if (normalized === 'session' || normalized === 'user') return 'session';
  return 'journey';
};

/** Purpose: create a unique ID for editor graph objects. Why: React Flow requires stable distinct node/edge IDs for selection and connections. */
const generateId = (prefix) => {
  const suffix = isBrowser && window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  return `${prefix}-${suffix}`;
};

/** Purpose: deep-clone JSON-compatible flow config. Why: normalization must not mutate API/store-owned objects while deriving editable node data. */
const clone = (value) => JSON.parse(JSON.stringify(value));

const LIBRARY_KEY_TO_API_TYPE = {
  quick_replies: 'quick_reply',
  message_quick_replies: 'quick_reply',
  whatsapp_list_prompt: 'list_message',
  message_whatsapp_list: 'list_message',
  dynamic_chat: 'genai_prompt',
  execute_flow: 'jump_to_flow',
  switch_flow: 'jump_to_flow',
  function: 'custom_function',
};

/** Purpose: infer the editor catalog key from a saved API node. Why: the backend's execution type can differ from the node type shown in the builder. */
const inferLibraryKeyFromApiNode = (node) => {
  if (node.config?.uiNodeKey) return node.config.uiNodeKey;
  if (node.type === 'quick_reply') return 'quick_replies';
  if (node.type === 'list_message') return 'message_whatsapp_list';
  if (node.type === 'genai_prompt') return 'dynamic_chat';
  if (node.type === 'jump_to_flow') return 'execute_flow';
  if (node.type === 'custom_function') return 'function';
  if (node.type === 'api_call') return 'api';

  if (node.type === 'question') {
    const validation = node.config?.validation;
    if (validation === 'email') return 'email';
    if (validation === 'phone') return 'phone';
    if (validation === 'name') return 'name';
    return 'question';
  }

  if (node.type === 'text_message') {
    const mediaType = node.config?.mediaType;
    if (mediaType === 'image') return 'image';
    if (mediaType === 'video') return 'video';
    if (mediaType === 'file') return 'file';
    return 'text';
  }

  return '';
};

/** Purpose: map a visual editor node to its backend execution type. Why: serialized flows must preserve the API schema expected by the runtime. */
const getApiTypeForNode = (node) => {
  const libraryMappedType = LIBRARY_KEY_TO_API_TYPE[node.data?.libraryKey];
  if (libraryMappedType) {
    return libraryMappedType;
  }

  if (node.type === 'start' || node.type === 'end' || node.type === 'condition' || node.type === 'api_call') {
    return node.type;
  }

  return node.type === 'input' ? 'question' : 'text_message';
};

/** Purpose: resolve input validation from explicit config or catalog type. Why: older nodes without an explicit validation field retain their intended behavior. */
const getValidationForNode = (node, config) => {
  if (config.validation) return config.validation;

  switch (node.data?.libraryKey) {
    case 'email':
      return 'email';
    case 'phone':
      return 'phone';
    case 'name':
      return 'name';
    case 'date':
      return 'date';
    default:
      return 'none';
  }
};

/** Purpose: convert routing tags from array or comma-separated input into a clean list. Why: handoff routing uses a predictable tag representation regardless of editor input format. */
const normalizeRoutingTags = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item || '').trim())
      .filter(Boolean);
  }

  return String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

/** Purpose: create a graph node using editor defaults plus optional template/overrides. Why: every palette-created or generated node needs a consistent React Flow shape. */
export const createGraphNode = (type, position = { x: 0, y: 0 }, overrides = {}) => {
  const template = overrides.template || null;
  const config = {
    ...(NODE_DEFAULT_CONFIG[type] || {}),
    ...(template?.config || {}),
    ...(overrides.config || {}),
  };

  return {
    // Database uses the API execution primitive internally, but its stable ID
    // should describe the product node instead of leaking that implementation.
    id: overrides.id || generateId(overrides.libraryKey === 'database' || template?.key === 'database' ? 'database' : type),
    type,
    position,
    selected: false,
    data: {
      title: overrides.title || NODE_LABELS[type] || 'Node',
      description: overrides.description || NODE_DESCRIPTIONS[type] || '',
      config,
      accent: overrides.accent,
      libraryKey: overrides.libraryKey || template?.key || '',
      category: overrides.category || template?.category || '',
      iconKey: overrides.iconKey || template?.key || '',
    },
    deletable: overrides.deletable !== undefined ? overrides.deletable : !['start', 'end'].includes(type),
  };
};

/** Purpose: construct the minimal new-flow graph. Why: new journeys start with required Start/End nodes and a valid initial connection. */
export const createStarterGraph = () => {
  const start = createGraphNode('start', { x: 110, y: 140 }, {
    id: 'start-node',
    title: 'Start',
    description: 'Entry point',
    config: { label: 'Start' },
    deletable: false,
  });

  const end = createGraphNode('end', { x: 110, y: 400 }, {
    id: 'end-node',
    title: 'End',
    description: 'Exit point',
    config: { label: 'End' },
  });

  return {
    flowId: '',
    flowName: 'Untitled flow',
    flowDescription: 'A clean starting point for your chatbot journey.',
    botId: '',
    environmentId: '',
    variables: [],
    entryNodeId: start.id,
    nodes: [start, end],
    edges: [
      {
        id: 'edge-start-end',
        source: start.id,
        target: end.id,
        label: 'next',
        type: 'smoothstep',
        animated: false,
        data: {
          condition: 'next',
        },
      },
    ],
    selectedNodeId: start.id,
  };
};

/** Purpose: translate backend node config into editor-facing title, metadata, and controls. Why: the canvas needs catalog-aware data without changing the persisted API node. */
const deriveNodeData = (node) => {
  const config = clone(node.config || {});
  const libraryKey = inferLibraryKeyFromApiNode(node);
  const template = libraryKey ? findFlowNodeItem(libraryKey) : null;
  const resolvedVariable = config.variable || config.variableName || '';
  const resolvedVariableName = config.variableName || config.variable || '';

  if (node.type === 'start') {
    return {
      title: 'Start',
      description: NODE_DESCRIPTIONS.start,
      libraryKey: 'start',
      category: 'start',
      iconKey: 'start',
      config: {
        label: node.label || 'Start',
      },
    };
  }

  if (node.type === 'end') {
    return {
      title: node.label || NODE_LABELS.end,
      description: NODE_DESCRIPTIONS.end,
      libraryKey: 'end',
      category: 'end',
      iconKey: 'end',
      config: {
        label: node.label || 'End',
        message: config.message || '',
        translations: config.translations && typeof config.translations === 'object' ? config.translations : {},
      },
    };
  }

  if (node.type === 'condition') {
    return {
      title: node.label || NODE_LABELS.condition,
      description: NODE_DESCRIPTIONS.condition,
      libraryKey: libraryKey || 'condition',
      category: template?.category || 'logic',
      iconKey: template?.key || 'condition',
      accent: template?.accent || '',
      config: {
        label: node.label || NODE_LABELS.condition,
        variable: resolvedVariable,
        variableName: resolvedVariableName,
        operator: config.operator || 'equals',
        value: config.value ?? '',
        conditions: Array.isArray(config.conditions) && config.conditions.length
          ? config.conditions
          : [{ conditionId: 'condition-0', variable: resolvedVariable, operator: config.conditionType || config.operator || 'equals', value: config.value ?? '', ignoreCase: false }],
      },
    };
  }

  if (node.type === 'question') {
    return {
      title: node.label || template?.label || NODE_LABELS.input,
      description: template?.description || NODE_DESCRIPTIONS.input,
      libraryKey: libraryKey || 'question',
      category: template?.category || 'prompts',
      iconKey: template?.key || 'question',
      accent: template?.accent || '',
      config: {
        label: node.label || template?.label || NODE_LABELS.input,
        prompt: config.question || config.prompt || '',
        variable: resolvedVariable,
        variableName: resolvedVariableName,
        placeholder: config.placeholder || 'Type your answer',
        required: Boolean(config.required),
        validation: config.validation || (
          libraryKey === 'email'
            ? 'email'
            : libraryKey === 'phone'
              ? 'phone'
              : libraryKey === 'name'
                ? 'name'
                : libraryKey === 'date'
                  ? 'date'
                  : 'none'
        ),
        dateValidation: config.dateValidation && typeof config.dateValidation === 'object'
          ? { ...config.dateValidation }
          : undefined,
        options: Array.isArray(config.options) ? config.options : ['Option 1', 'Option 2'],
        listItems: Array.isArray(config.listItems) ? config.listItems : [],
        translations: config.translations && typeof config.translations === 'object' ? config.translations : {},
        localizationKey: config.localizationKey || '',
      },
    };
  }

  if (node.type === 'quick_reply') {
    return {
      title: node.label || template?.label || 'Quick replies',
      description: template?.description || 'Capture a guided response with ready-made choices.',
      libraryKey: libraryKey || 'quick_replies',
      category: template?.category || 'prompts',
      iconKey: template?.key || 'quick_replies',
      accent: template?.accent || '',
      config: {
        label: node.label || template?.label || 'Quick replies',
        prompt: config.prompt || config.question || config.message || '',
        message: config.prompt || config.question || config.message || '',
        variable: resolvedVariable,
        variableName: resolvedVariableName,
        placeholder: config.placeholder || 'Pick one',
        required: config.required !== undefined ? Boolean(config.required) : true,
        validation: config.validation || 'quick_reply',
        options: Array.isArray(config.options) ? config.options : ['Option 1', 'Option 2'],
        translations: config.translations && typeof config.translations === 'object' ? config.translations : {},
        localizationKey: config.localizationKey || '',
      },
    };
  }

  if (node.type === 'list_message') {
    return {
      title: node.label || template?.label || 'WhatsApp List',
      description: template?.description || 'Display an interactive list of items.',
      libraryKey: libraryKey || 'message_whatsapp_list',
      category: template?.category || 'message',
      iconKey: template?.key || 'message_whatsapp_list',
      accent: template?.accent || '',
      config: {
        label: node.label || template?.label || 'WhatsApp List',
        title: config.title || config.prompt || config.message || '',
        prompt: config.prompt || config.title || config.message || '',
        message: config.message || config.prompt || config.title || '',
        variable: resolvedVariable,
        variableName: resolvedVariableName,
        required: Boolean(config.required),
        listItems: Array.isArray(config.listItems) ? config.listItems : [],
        translations: config.translations && typeof config.translations === 'object' ? config.translations : {},
        localizationKey: config.localizationKey || '',
      },
    };
  }

  if (node.type === 'api_call') {
    return {
      title: node.label || template?.label || NODE_LABELS.api_call,
      description: template?.description || NODE_DESCRIPTIONS.api_call,
      libraryKey: libraryKey || 'api',
      category: template?.category || 'actions',
      iconKey: template?.key || 'api',
      accent: template?.accent || '',
      config: {
        label: node.label || template?.label || NODE_LABELS.api_call,
        asyncRequest: {
          ...(config.asyncRequest || {}),
          apiDefinitionId: config.asyncRequest?.apiDefinitionId || config.apiDefinitionId || '',
          apiDefinitionName: config.asyncRequest?.apiDefinitionName || config.apiDefinitionName || '',
          variableMappings: config.asyncRequest?.variableMappings || config.variableMappings || {},
          url: config.asyncRequest?.url || config.url || '',
          method: config.asyncRequest?.method || config.method || 'POST',
          headers: config.asyncRequest?.headers || config.headers || {},
          query: config.asyncRequest?.query || config.query || {},
          body: config.asyncRequest?.body ?? config.body ?? {},
          pathParams: config.asyncRequest?.pathParams || config.pathParams || {},
          auth: config.asyncRequest?.auth || config.auth || { type: 'none' },
          timeout: Number(config.asyncRequest?.timeout ?? config.timeout ?? 10000),
          retries: Number(config.asyncRequest?.retries ?? config.retries ?? 2),
          retryDelayMs: Number(config.asyncRequest?.retryDelayMs ?? config.retryDelayMs ?? 300),
          responseVariable: config.asyncRequest?.responseVariable || config.responseVariable || '',
        },
        url: config.url || '',
        method: config.method || config.asyncRequest?.method || 'POST',
        apiDefinitionId: config.apiDefinitionId || config.asyncRequest?.apiDefinitionId || '',
        apiDefinitionName: config.apiDefinitionName || config.asyncRequest?.apiDefinitionName || '',
        headers: config.headers || config.asyncRequest?.headers || {},
        body: config.body || config.asyncRequest?.body || {},
        query: config.query || config.asyncRequest?.query || {},
        pathParams: config.pathParams || config.asyncRequest?.pathParams || {},
        auth: config.auth || config.asyncRequest?.auth || { type: 'none' },
        variableMappings: config.variableMappings || config.asyncRequest?.variableMappings || {},
        timeout: Number(config.asyncRequest?.timeout ?? config.timeout ?? 10000),
        retries: Number(config.asyncRequest?.retries ?? config.retries ?? 2),
        retryDelayMs: Number(config.asyncRequest?.retryDelayMs ?? config.retryDelayMs ?? 300),
        responseVariable: config.responseVariable || config.asyncRequest?.responseVariable || 'api_result',
        responseMessage: config.responseMessage || '',
        responseMapping: Array.isArray(config.responseMapping) ? config.responseMapping : [],
        variableAssignments: Array.isArray(config.variableAssignments) ? config.variableAssignments : [],
        customStorage: config.customStorage || {
          enabled: false,
          operation: 'create',
          tableId: '',
          keyField: '',
          limit: 25,
          failOnEmptyResult: true,
          filters: {},
          responseVariable: '',
          payload: {},
        },
        code: config.code || '',
        outputVariable: config.outputVariable || '',
        targetFlowId: config.targetFlowId || '',
        returnAfterJump: Boolean(config.returnAfterJump),
        switchPayload: config.switchPayload && typeof config.switchPayload === 'object' ? config.switchPayload : {},
      },
    };
  }

  if (node.type === 'custom_function') {
    return {
      title: node.label || template?.label || 'Function',
      description: template?.description || 'Run a custom JavaScript function.',
      libraryKey: libraryKey || 'function',
      category: template?.category || 'actions',
      iconKey: template?.key || 'function',
      accent: template?.accent || '',
      config: {
        label: node.label || template?.label || 'Function',
        functionDefinitionId: config.functionDefinitionId || '',
        functionDefinitionName: config.functionDefinitionName || '',
        variableMappings: config.variableMappings || {},
        code: config.code || '',
        outputVariable: config.outputVariable || '',
        timeout: Number(config.timeout ?? 5000),
        responseMapping: Array.isArray(config.responseMapping) ? config.responseMapping : [],
      },
    };
  }

  if (node.type === 'jump_to_flow') {
    return {
      title: node.label || template?.label || 'Execute Flow',
      description: template?.description || 'Jump into another flow.',
      libraryKey: libraryKey || 'execute_flow',
      category: template?.category || 'actions',
      iconKey: template?.key || 'execute_flow',
      accent: template?.accent || '',
      config: {
        label: node.label || template?.label || 'Execute Flow',
        message: config.message || '',
        targetFlowId: config.targetFlowId || '',
        returnAfterJump: Boolean(config.returnAfterJump),
        switchPayload: config.switchPayload && typeof config.switchPayload === 'object' ? config.switchPayload : {},
      },
    };
  }

  return {
    title: node.label || template?.label || NODE_LABELS.message,
    description: template?.description || NODE_DESCRIPTIONS.message,
    libraryKey: libraryKey || 'text',
    category: template?.category || 'message',
    iconKey: template?.key || 'text',
    accent: template?.accent || '',
    config: {
      label: node.label || template?.label || NODE_LABELS.message,
      message: config.message || config.prompt || '',
      prompt: config.prompt || config.message || '',
      variable: resolvedVariable,
      variableName: resolvedVariableName,
      localizationKey: config.localizationKey || '',
      mediaUrl: config.mediaUrl || '',
      mediaType: config.mediaType || 'text',
      alt: config.alt || '',
      options: Array.isArray(config.options) ? config.options : ['Option 1', 'Option 2'],
      listItems: Array.isArray(config.listItems) ? config.listItems : [],
      title: config.title || '',
      systemPrompt: config.systemPrompt || '',
      conversationMode: config.conversationMode === true,
      contextVariable: config.contextVariable || '',
      includePreviousHistory: config.includePreviousHistory === true,
      historyMessageLimit: config.historyMessageLimit ?? 5,
      historyUnit: config.historyUnit === 'turns' ? 'turns' : 'messages',
      maxTurns: config.maxTurns ?? 40,
      collectFields: Array.isArray(config.collectFields) ? config.collectFields : [],
      workflowSkills: Array.isArray(config.workflowSkills) ? config.workflowSkills : [],
      failureMessage: config.failureMessage || '',
      allowedSkills: Array.isArray(config.allowedSkills) ? config.allowedSkills : [],
      confirmationMessage: config.confirmationMessage || '',
      completionMessage: config.completionMessage || '',
      exitMessage: config.exitMessage || '',
      handoffMessage: config.handoffMessage || '',
      temperature: config.temperature ?? 0.7,
      maxTokens: config.maxTokens ?? 150,
      responseVariable: config.responseVariable || '',
      responseMapping: Array.isArray(config.responseMapping) ? config.responseMapping : [],
      targetFlowId: config.targetFlowId || '',
      returnAfterJump: Boolean(config.returnAfterJump),
      switchPayload: config.switchPayload && typeof config.switchPayload === 'object' ? config.switchPayload : {},
      code: config.code || '',
      outputVariable: config.outputVariable || '',
      provider: config.provider || 'native',
      name: config.name || '',
      query: typeof config.query === 'string' ? config.query : '',
      mobile: config.mobile || '',
      email: config.email || '',
      queueId: config.queueId || '',
      skills: Array.isArray(config.skills) ? config.skills : [],
      skillMatch: config.skillMatch || 'all',
      language: config.language || '',
      languageCode: config.languageCode || '',
      translations: config.translations && typeof config.translations === 'object' ? config.translations : {},
      assignmentStrategy: config.assignmentStrategy || 'least_active',
      customFields: config.customFields && typeof config.customFields === 'object' ? config.customFields : {},
      assignmentMode: config.assignmentMode || 'auto',
      noAgentMessage: config.noAgentMessage || '',
      assignedMessage: config.assignedMessage || '',
      handoffResultVariable: config.handoffResultVariable || '',
      priority: config.priority || 'medium',
      queueName: config.queueName || '',
      department: config.department || '',
      groupName: config.groupName || '',
      routingTags: normalizeRoutingTags(config.routingTags),
    },
  };
};

/** Purpose: ensure a graph has required Start/End boundaries and terminal connections. Why: incomplete or older saved flows must remain editable and have a valid exit path. */
const ensureUiBoundaryGraph = (nodes = [], edges = [], entryNodeId = '') => {
  const nextNodes = [...nodes];
  const nextEdges = [...edges];
  let startNode = nextNodes.find((node) => node.type === 'start') || null;
  let endNode = nextNodes.find((node) => node.type === 'end') || null;

  if (!startNode) {
    startNode = createGraphNode('start', { x: 110, y: 140 }, {
      id: 'start-node',
      title: 'Start',
      description: 'Entry point',
      config: { label: 'Start' },
      deletable: false,
    });
    nextNodes.unshift(startNode);
  }

  if (!endNode) {
    const lastNode = nextNodes[nextNodes.length - 1] || startNode;
    endNode = createGraphNode('end', {
      x: lastNode?.position?.x ?? 110,
      y: (lastNode?.position?.y ?? 140) + 240,
    }, {
      id: 'end-node',
      title: 'End',
      description: 'Exit point',
      config: { label: 'End' },
      deletable: false,
    });
    nextNodes.push(endNode);
  }

  // Purpose: check whether the graph already links a terminal node to End. Why: boundary repair must be idempotent and avoid duplicate edges.
  const edgeExists = (source, target) => nextEdges.some((edge) => edge.source === source && edge.target === target);
  /** Purpose: Find nonterminal nodes that have no outgoing connections. Why: Boundary detection supports automatic edge creation while leaving explicit End nodes alone. */
  const terminalNodes = nextNodes.filter((node) => {
    if (node.id === endNode.id) return false;
    return !nextEdges.some((edge) => edge.source === node.id);
  });

  terminalNodes.forEach((node, index) => {
    if (!edgeExists(node.id, endNode.id)) {
      nextEdges.push({
        id: `edge-${node.id}-${endNode.id}-${index}`,
        source: node.id,
        target: endNode.id,
        label: 'next',
        type: 'smoothstep',
        data: {
          condition: 'next',
        },
      });
    }
  });

  return {
    nodes: nextNodes.map((node) => ({
      ...node,
      deletable: !['start', 'end'].includes(node.type),
    })),
    edges: nextEdges,
    entryNodeId: entryNodeId || startNode.id,
  };
};

/** Purpose: convert the saved API flow into a normalized editor graph. Why: load supports legacy node schemas and repairs required UI boundaries before interaction. */
export const hydrateFlowFromApi = (flow) => {
  /** Purpose: Convert persisted flow nodes into canvas nodes with positions and derived display data. Why: Loading must preserve runtime node configuration while adapting the editor representation. */
  const hydratedNodes = (flow?.nodes || []).map((node, index) => {
    const uiType = API_TO_UI_TYPE[node.type] || 'message';
    const position = node.position || {
      x: 140 + index * 260,
      y: 130 + (index % 2) * 160,
    };

    return {
      id: node.nodeId || generateId(uiType),
      type: uiType,
      position,
      deletable: uiType !== 'start',
      data: deriveNodeData(node),
    };
  });

  /** Purpose: Convert persisted connections into canvas edges with branch handles and conditions. Why: The editor must reconstruct the same outcome routes when reopening a saved flow. */
  const hydratedEdges = (flow?.nodes || []).flatMap((node) => {
    const source = node.nodeId;
    const isApiBranchNode = (node.type === 'api_call' && node.config?.uiNodeKey !== 'function') || node.config?.uiNodeKey === 'dynamic_chat';
    return (node.connections || []).map((connection, index) => {
      const condition = String(connection.condition || connection.label || 'next').trim().toLowerCase();
      const apiFailure = isApiBranchNode && ['failure', 'error', 'false'].includes(condition);
      const conversationBranch = node.config?.conversationMode && ['exit', 'handoff'].includes(condition) ? condition : '';
      const apiBranch = conversationBranch || (isApiBranchNode ? (apiFailure ? 'failure' : 'success') : '');
      const apiColor = apiFailure ? '#fb7185' : '#34d399';
      return {
        id: `edge-${source}-${connection.targetNodeId}-${index}`,
        source,
        target: connection.targetNodeId,
        sourceHandle: apiBranch || undefined,
        label: conversationBranch || (isApiBranchNode ? (apiFailure ? 'Failure' : 'Success') : connection.label || connection.condition || ''),
        data: {
          condition: apiBranch || connection.condition || 'next',
        },
        ...(isApiBranchNode ? {
          type: 'smoothstep',
          animated: true,
          markerEnd: { type: 'arrowclosed', color: apiColor },
          style: { stroke: apiColor, strokeWidth: 2 },
        } : {}),
      };
    });
  });

  const starter = createStarterGraph();
  const hasNodes = hydratedNodes.length > 0;
  const normalizedGraph = hasNodes
    ? ensureUiBoundaryGraph(hydratedNodes, hydratedEdges, flow?.entryNodeId || '')
    : ensureUiBoundaryGraph(starter.nodes, starter.edges, starter.entryNodeId);

  return {
    flowId: flow?.flowId || '',
    flowName: flow?.name || 'Untitled flow',
    flowDescription: flow?.description || '',
    botId: flow?.botId || '',
    environmentId: flow?.environmentId || '',
    variables: Array.isArray(flow?.variables)
      ? flow.variables.map((item) => ({
          name: String(item?.name || '').trim(),
          scope: normalizeVariableScope(item?.scope || 'journey'),
          type: item?.type || 'string',
          defaultValue: item?.defaultValue ?? '',
          description: item?.description || '',
        })).filter((item) => item.name)
      : [],
    entryNodeId: normalizedGraph.entryNodeId,
    nodes: normalizedGraph.nodes,
    edges: normalizedGraph.edges,
    selectedNodeId: normalizedGraph.nodes[0]?.id || starter.selectedNodeId,
  };
};

/** Purpose: convert editor state back to the backend flow payload. Why: saving must preserve API node types, connections, and configuration while omitting UI-only state. */
export const serializeFlowToApi = (state) => {
  /** Purpose: Serialize canvas nodes and their outgoing connections into flow records. Why: Persistence must retain node config and represent each route once. */
  const nodes = (state.nodes || []).map((node) => {
    const config = node.data?.config || {};
    /** Purpose: Serialize an edge into a runtime connection record. Why: The engine consumes target node IDs and conditions rather than canvas edge objects. */
    const connections = (state.edges || [])
      .filter((edge) => edge.source === node.id)
      .map((edge) => ({
        targetNodeId: edge.target,
        condition: edge.data?.condition || edge.label || 'next',
        label: edge.label || '',
      }));

    const apiType = getApiTypeForNode(node);
    const resolvedVariable = config.variable || config.variableName || '';
    const resolvedVariableName = config.variableName || config.variable || '';
    const baseConfig = {
      uiNodeKey: node.data?.libraryKey || '',
      localizationKey: String(config.localizationKey || '').trim().toLowerCase(),
      variable: resolvedVariable,
      variableName: resolvedVariableName,
    };
    const apiNode = {
      nodeId: node.id,
      type: apiType,
      label: config.label || node.data?.title || NODE_LABELS[node.type] || 'Node',
      position: {
        x: Math.round(node.position?.x || 0),
        y: Math.round(node.position?.y || 0),
      },
      config: baseConfig,
      connections,
    };

    if (apiType === 'text_message') {
      apiNode.config = {
        ...baseConfig,
        message: config.message || '',
        mediaUrl: config.mediaUrl || '',
        mediaType: config.mediaType || 'text',
        alt: config.alt || '',
        provider: config.provider || 'native',
        name: config.name || '',
        query: typeof config.query === 'string' ? config.query : '',
        mobile: config.mobile || '',
        email: config.email || '',
        queueId: config.queueId || '',
        skills: Array.isArray(config.skills) ? config.skills : [],
        skillMatch: config.skillMatch || 'all',
        language: config.language || '',
        languageCode: config.languageCode || '',
        translations: config.translations && typeof config.translations === 'object' ? config.translations : {},
        assignmentStrategy: config.assignmentStrategy || 'least_active',
        customFields: config.customFields && typeof config.customFields === 'object' ? config.customFields : {},
        assignmentMode: config.assignmentMode || 'auto',
        noAgentMessage: config.noAgentMessage || '',
        assignedMessage: config.assignedMessage || '',
        handoffResultVariable: config.handoffResultVariable || '',
        resumeOnResolve: config.resumeOnResolve !== false,
        resolvedMessage: config.resolvedMessage || '',
        queueWaitSeconds: config.queueWaitSeconds === '' || config.queueWaitSeconds === undefined ? '' : Number(config.queueWaitSeconds),
        priority: config.priority || 'medium',
        queueName: config.queueName || '',
        department: config.department || '',
        groupName: config.groupName || '',
        routingTags: normalizeRoutingTags(config.routingTags),
      };
    } else if (apiType === 'quick_reply') {
      apiNode.config = {
        ...baseConfig,
        prompt: config.prompt || config.message || '',
        question: config.prompt || config.message || '',
        variable: resolvedVariable,
        variableName: resolvedVariableName,
        required: Boolean(config.required),
        validation: getValidationForNode(node, config),
        translations: config.translations && typeof config.translations === 'object' ? config.translations : {},
        options: Array.isArray(config.options)
          ? config.options.filter(Boolean).map((option, index) => (
              typeof option === 'object' && option !== null
                ? {
                    title: String(option.title ?? option.label ?? option.text ?? option.value ?? `Option ${index + 1}`),
                    text: String(option.text ?? option.value ?? option.title ?? option.label ?? ''),
                    localizationKey: String(option.localizationKey || '').trim().toLowerCase(),
                  }
                : String(option)
            ))
          : [],
      };
    } else if (apiType === 'list_message') {
      apiNode.config = {
        ...baseConfig,
        title: config.title || config.prompt || config.message || '',
        prompt: config.title || config.prompt || config.message || '',
        variable: resolvedVariable,
        variableName: resolvedVariableName,
        required: Boolean(config.required),
        translations: config.translations && typeof config.translations === 'object' ? config.translations : {},
        listItems: Array.isArray(config.listItems) ? config.listItems.filter((item) => item?.label || item?.value) : [],
      };
    } else if (apiType === 'genai_prompt') {
      apiNode.config = {
        ...baseConfig,
        prompt: config.prompt || config.message || '',
        systemPrompt: config.systemPrompt || '',
        temperature: Number(config.temperature ?? 0.7),
        maxTokens: Number(config.maxTokens ?? 150),
        responseVariable: config.responseVariable || '',
        failureMessage: config.failureMessage || '',
        allowedSkills: Array.isArray(config.allowedSkills) ? config.allowedSkills.filter(Boolean) : [],
        conversationMode: config.conversationMode === true,
        contextVariable: config.contextVariable || '',
        includePreviousHistory: config.includePreviousHistory === true,
        historyMessageLimit: Number(config.historyMessageLimit ?? 5),
        historyUnit: config.historyUnit === 'turns' ? 'turns' : 'messages',
        maxTurns: Number(config.maxTurns || 40),
        collectFields: config.collectFields || [],
        workflowSkills: config.workflowSkills || [],
        confirmationMessage: config.confirmationMessage || '',
        completionMessage: config.completionMessage || '',
        exitMessage: config.exitMessage || '',
        handoffMessage: config.handoffMessage || '',
      };
    } else if (apiType === 'api_call') {
      apiNode.config = {
        ...baseConfig,
        apiDefinitionId: config.asyncRequest?.apiDefinitionId || config.apiDefinitionId || '',
        apiDefinitionName: config.asyncRequest?.apiDefinitionName || config.apiDefinitionName || '',
        url: config.asyncRequest?.url || config.url || '',
        method: config.asyncRequest?.method || config.method || 'POST',
        headers: config.asyncRequest?.headers || config.headers || {},
        body: config.asyncRequest?.body || config.body || {},
        query: config.asyncRequest?.query || config.query || {},
        pathParams: config.asyncRequest?.pathParams || config.pathParams || {},
        auth: config.asyncRequest?.auth || config.auth || { type: 'none' },
        variableMappings: config.asyncRequest?.variableMappings || config.variableMappings || {},
        timeout: Number(config.asyncRequest?.timeout ?? config.timeout ?? 10000),
        retries: Number(config.asyncRequest?.retries ?? config.retries ?? 2),
        retryDelayMs: Number(config.asyncRequest?.retryDelayMs ?? config.retryDelayMs ?? 300),
        responseVariable: config.asyncRequest?.responseVariable || config.responseVariable || '',
        responseMessage: config.responseMessage || '',
        responseMapping: Array.isArray(config.responseMapping) ? config.responseMapping : [],
        variableAssignments: Array.isArray(config.variableAssignments) ? config.variableAssignments : [],
        customStorage: config.customStorage || {
          enabled: false,
          operation: 'create',
          tableId: '',
          keyField: '',
          limit: 25,
          failOnEmptyResult: true,
          filters: {},
          responseVariable: '',
          payload: {},
        },
      };
    } else if (apiType === 'custom_function') {
      apiNode.config = {
        ...baseConfig,
        functionDefinitionId: config.functionDefinitionId || '',
        functionDefinitionName: config.functionDefinitionName || '',
        variableMappings: config.variableMappings || {},
        code: config.code || '',
        outputVariable: config.outputVariable || '',
        timeout: Number(config.timeout ?? 5000),
        responseMapping: Array.isArray(config.responseMapping) ? config.responseMapping : [],
      };
    } else if (apiType === 'jump_to_flow') {
      apiNode.config = {
        ...baseConfig,
        targetFlowId: config.targetFlowId || '',
        returnAfterJump: Boolean(config.returnAfterJump),
        switchPayload: config.switchPayload && typeof config.switchPayload === 'object' ? config.switchPayload : {},
      };
    } else if (apiType === 'question') {
      apiNode.config = {
        ...baseConfig,
        question: config.prompt || '',
        variable: resolvedVariable,
        variableName: resolvedVariableName,
        required: Boolean(config.required),
        translations: config.translations && typeof config.translations === 'object' ? config.translations : {},
        validation: getValidationForNode(node, config),
        dateValidation: config.dateValidation && typeof config.dateValidation === 'object'
          ? { ...config.dateValidation }
          : undefined,
        placeholder: config.placeholder || '',
      };
    } else if (apiType === 'condition') {
      apiNode.config = {
        ...baseConfig,
        conditionType: config.operator || 'equals',
        variable: resolvedVariable,
        variableName: resolvedVariableName,
        value: config.value ?? '',
        conditions: Array.isArray(config.conditions) && config.conditions.length
          ? config.conditions
          : [{ conditionId: 'condition-0', variable: resolvedVariable, operator: config.operator || 'equals', value: config.value ?? '', ignoreCase: false }],
      };
    } else if (apiType === 'start') {
      apiNode.config = { ...baseConfig, label: config.label || 'Start' };
    } else if (apiType === 'end') {
      apiNode.config = {
        ...baseConfig,
        label: config.label || 'End',
        message: config.message || '',
        translations: config.translations && typeof config.translations === 'object' ? config.translations : {},
      };
    }

    return apiNode;
  });

  return {
    name: state.flowName || 'Untitled flow',
    description: state.flowDescription || '',
    botId: state.botId || '',
    environmentId: state.environmentId || '',
    entryNodeId: state.entryNodeId || state.nodes?.[0]?.id || '',
    nodes,
    triggers: [],
    variables: Array.isArray(state.variables)
      ? state.variables
          .map((item) => ({
            name: String(item?.name || '').trim(),
            scope: normalizeVariableScope(item?.scope || 'journey'),
            type: item?.type || 'string',
            defaultValue: item?.defaultValue ?? '',
            description: item?.description || '',
          }))
          .filter((item) => item.name)
      : [],
    isActive: true,
  };
};

/** Purpose: Create the shared editor state and graph actions. Why: A single store keeps canvas, inspector, undo, and persistence views synchronized. */
const useFlowBuilderStore = create((set, get) => ({
  flowId: '',
  flowName: 'Untitled flow',
  flowDescription: '',
  botId: '',
  environmentId: '',
  variables: [],
  entryNodeId: '',
  nodes: createStarterGraph().nodes,
  edges: createStarterGraph().edges,
  selectedNodeId: createStarterGraph().selectedNodeId,
  paletteCollapsed: true,
  inspectorCollapsed: true,
  recentNodeKeys: [],
  loadingFlow: false,
  savingFlow: false,
  error: '',
  notice: '',
  headerActions: null,

  // Purpose: update flow-level metadata. Why: title/description edits should not replace graph state.
  setFlowMeta: (patch) => set((state) => ({ ...state, ...patch })),
  // Purpose: apply a general flow-state patch. Why: async load/save paths update several related fields atomically.
  setFlowData: (patch) => set((state) => ({ ...state, ...patch })),
  // Purpose: replace variable definitions with a validated array shape. Why: consumers can rely on variables always being iterable.
  setVariables: (variables) => set({ variables: Array.isArray(variables) ? variables : [] }),
  // Purpose: append a normalized variable definition. Why: new flow variables need consistent defaults and a non-empty name.
  addVariable: (variable) => set((state) => ({
    variables: [
      ...state.variables,
      {
        name: String(variable?.name || '').trim(),
        scope: normalizeVariableScope(variable?.scope || 'journey'),
        type: variable?.type || 'string',
        defaultValue: variable?.defaultValue ?? '',
        description: variable?.description || '',
      },
    ].filter((item) => item.name),
  })),
  // Purpose: update one variable while preserving unedited fields. Why: scope/name normalization keeps references stable across the editor.
  updateVariable: (index, patch) => set((state) => ({
    variables: state.variables.map((item, currentIndex) => (
      currentIndex === index
        ? {
            ...item,
            ...patch,
            name: String((patch?.name ?? item?.name) || '').trim(),
            scope: normalizeVariableScope(patch?.scope ?? item?.scope ?? 'journey'),
          }
        : item
    )),
  })),
  // Purpose: remove a variable by its current list position. Why: the manager reports row indexes and the store owns the canonical list.
  removeVariable: (index) => set((state) => ({
    variables: state.variables.filter((_, currentIndex) => currentIndex !== index),
  })),
  // Purpose: replace or functionally update graph nodes. Why: supports React Flow change batches without stale state closures.
  setNodes: (nodes) => set((state) => ({ nodes: typeof nodes === 'function' ? nodes(state.nodes) : nodes })),
  // Purpose: replace or functionally update graph edges. Why: keeps connection updates consistent with the latest graph state.
  setEdges: (edges) => set((state) => ({ edges: typeof edges === 'function' ? edges(state.edges) : edges })),
  // Purpose: select a node and reveal its inspector. Why: choosing a canvas item should make its settings immediately available.
  setSelectedNodeId: (selectedNodeId) => set({ selectedNodeId, inspectorCollapsed: false }),
  // Purpose: clear the current node selection. Why: panels and keyboard actions need an explicit no-selection state.
  clearSelection: () => set({ selectedNodeId: '' }),
  // Purpose: set the node palette's collapsed state. Why: canvas layout controls must be shared and predictable across editor views.
  setPaletteCollapsed: (paletteCollapsed) => set({ paletteCollapsed: Boolean(paletteCollapsed) }),
  // Purpose: set the property inspector's collapsed state. Why: the canvas can reclaim space without losing selected-node data.
  setInspectorCollapsed: (inspectorCollapsed) => set({ inspectorCollapsed: Boolean(inspectorCollapsed) }),
  // Purpose: invert palette visibility. Why: toolbar controls can toggle the side panel without reading state themselves.
  togglePalette: () => set((state) => ({ paletteCollapsed: !state.paletteCollapsed })),
  // Purpose: invert inspector visibility. Why: one action preserves consistent toggle behavior for all editor controls.
  toggleInspector: () => set((state) => ({ inspectorCollapsed: !state.inspectorCollapsed })),
  // Purpose: track flow-loading progress. Why: the UI should distinguish an empty graph from a graph still being fetched.
  setLoadingFlow: (loadingFlow) => set({ loadingFlow }),
  // Purpose: track flow-saving progress. Why: save controls can prevent duplicate submissions and show feedback.
  setSavingFlow: (savingFlow) => set({ savingFlow }),
  // Purpose: store or clear the current editor error. Why: async and validation failures need a consistent page-level display.
  setError: (error) => set({ error: error || '' }),
  // Purpose: store or clear an editor success/info notice. Why: non-error feedback should not be mixed with validation failures.
  setNotice: (notice) => set({ notice: notice || '' }),
  // Purpose: publish header controls to the shared app shell. Why: the layout renders flow actions outside the canvas component tree.
  setHeaderActions: (headerActions) => set({ headerActions }),
  // Purpose: remove injected flow header controls. Why: stale actions must not leak into another route after leaving the editor.
  clearHeaderActions: () => set({ headerActions: null }),

  editorLoadRevision: 0,
  // Purpose: load API data into editor state and advance its revision. Why: history/selection effects can detect a fresh flow load and reset safely.
  hydrateFromApi: (flow) => set((state) => ({ ...hydrateFlowFromApi(flow), editorLoadRevision: state.editorLoadRevision + 1, error: '', notice: '' })),
  // Purpose: restore a clean starter graph and advance the load revision. Why: new-flow/reset actions should clear prior errors while refreshing editor history.
  resetToStarter: () => set((state) => ({ ...createStarterGraph(), editorLoadRevision: (state.editorLoadRevision || 0) + 1, error: '', notice: '' })),

  // Purpose: patch one node's title/config while retaining compatible field aliases. Why: message and prompt settings are used by multiple node schemas and must remain synchronized.
  updateNodeData: (nodeId, updater) => set((state) => ({
    nodes: state.nodes.map((node) => {
      if (node.id !== nodeId) return node;
      const current = {
        ...node,
        data: {
          ...node.data,
          config: {
            ...(node.data?.config || {}),
          },
        },
      };
      const next = typeof updater === 'function' ? updater(current) : updater;
      const nextTitle = next?.data?.title ?? current.data?.title;
      const nextConfig = {
        ...(current.data?.config || {}),
        ...(next?.data?.config || {}),
      };

      nextConfig.label = next?.data?.config?.label || nextTitle || nextConfig.label;

      if (node.type === 'message') {
        if (next?.data?.config?.message !== undefined && next?.data?.config?.prompt === undefined) {
          nextConfig.prompt = next.data.config.message;
        } else if (next?.data?.config?.prompt !== undefined && next?.data?.config?.message === undefined) {
          nextConfig.message = next.data.config.prompt;
        }
      }

      if (node.type === 'input') {
        if (next?.data?.config?.prompt !== undefined && next?.data?.config?.message === undefined) {
          nextConfig.message = next.data.config.prompt;
        } else if (next?.data?.config?.message !== undefined && next?.data?.config?.prompt === undefined) {
          nextConfig.prompt = next.data.config.message;
        }
      }

      return {
        ...current,
        ...next,
        data: {
          ...current.data,
          ...(next?.data || {}),
          title: nextTitle,
          config: nextConfig,
        },
      };
    }),
  })),

  // Move a choice and its canvas ports together so undo/save cannot capture mismatched routes.
  reorderQuickReplyOptions: (nodeId, fromIndex, toIndex) => set((state) => {
    const node = state.nodes.find((item) => item.id === nodeId);
    const options = node?.data?.config?.options;
    if (!Array.isArray(options) || !Number.isInteger(fromIndex) || !Number.isInteger(toIndex)
      || fromIndex < 0 || toIndex < 0 || fromIndex >= options.length || toIndex >= options.length
      || fromIndex === toIndex) return state;

    const order = options.map((_, index) => index);
    order.splice(toIndex, 0, order.splice(fromIndex, 1)[0]);
    const values = options.map((option) => String(typeof option === 'object' && option !== null
      ? option.text ?? option.value ?? option.title ?? option.label ?? '' : option ?? '').trim().toLowerCase());
    return {
      nodes: state.nodes.map((item) => item.id !== nodeId ? item : {
        ...item, data: { ...item.data, config: { ...item.data.config, options: order.map((index) => options[index]) } },
      }),
      edges: state.edges.map((edge) => {
        if (edge.source !== nodeId) return edge;
        const condition = String(edge.data?.condition || edge.label || '').trim().toLowerCase();
        if (condition === 'fallback' || edge.sourceHandle === 'quick-reply-fallback') return edge;
        const matches = values.map((value, index) => value && value === condition ? index : -1).filter((index) => index >= 0);
        const handle = String(edge.sourceHandle || '').match(/^quick-reply-(\d+)$/);
        // Stored conditions also cover older flows with missing or incorrect positional handles.
        const oldIndex = matches.length === 1 ? matches[0] : handle ? Number(handle[1]) : -1;
        const newIndex = order.indexOf(oldIndex);
        return newIndex < 0 ? edge : { ...edge, sourceHandle: `quick-reply-${newIndex}` };
      }),
    };
  }),

  // Purpose: update a node's canvas coordinates. Why: dragging should persist position changes without rebuilding its config.
  updateNodePosition: (nodeId, position) => set((state) => ({
    nodes: state.nodes.map((node) => (
      node.id === nodeId ? { ...node, position } : node
    )),
  })),

  // Purpose: create and select a palette/template node. Why: node defaults, recent usage, and inspector state must update together.
  addNode: (type, position, options = {}) => {
    const template = options.template || (options.libraryKey ? findFlowNodeItem(options.libraryKey) : null);
    const node = createGraphNode(type, position, {
      ...options,
      template,
      title: options.title || template?.label || options.title,
      description: options.description || template?.description || options.description,
      libraryKey: options.libraryKey || template?.key || '',
      category: options.category || template?.category || '',
      accent: options.accent || template?.accent || '',
      config: {
        ...(template?.config || {}),
        ...(options.config || {}),
      },
    });
    set((state) => ({
      nodes: [...state.nodes, node],
      selectedNodeId: node.id,
      inspectorCollapsed: false,
      recentNodeKeys: options.libraryKey
        ? [options.libraryKey, ...state.recentNodeKeys.filter((key) => key !== options.libraryKey)].slice(0, 8)
        : state.recentNodeKeys,
    }));
    return node;
  },

  // Purpose: clone an eligible node with a new ID and offset position. Why: authors can reuse configuration without creating graph identity collisions.
  duplicateNode: (nodeId) => {
    let duplicatedNode = null;
    set((state) => {
      /** Purpose: Resolve a node by ID for a store operation. Why: Graph actions must work against the current canonical node list. */
      const target = state.nodes.find((node) => node.id === nodeId);
      if (!target || target.type === 'start') {
        return state;
      }
      const newId = generateId(target.data?.libraryKey === 'database' ? 'database' : (target.type || 'node'));
      duplicatedNode = {
        ...clone(target),
        id: newId,
        position: {
          x: (target.position?.x || 0) + 40,
          y: (target.position?.y || 0) + 40,
        },
        data: {
          ...clone(target.data || {}),
          title: target.data?.title ? `${target.data.title} (Copy)` : 'Node (Copy)',
          nodeId: newId,
        },
        selected: true,
      };
      return {
        nodes: [...state.nodes.map((n) => ({ ...n, selected: false })), duplicatedNode],
        selectedNodeId: newId,
        inspectorCollapsed: false,
      };
    });
    return duplicatedNode;
  },

  // Purpose: remove a deletable node and its attached edges. Why: graph integrity requires no connections to point at a deleted node.
  deleteNode: (nodeId) => set((state) => {
    /** Purpose: Check whether the target node exists before deletion. Why: Deleting a missing node should not create misleading store changes. */
    const target = state.nodes.find((node) => node.id === nodeId);
    if (!target || target.type === 'start' || target.deletable === false) {
      return state;
    }
    /** Purpose: Remove the requested node from the graph. Why: All graph operations should derive from the canonical node collection. */
    const nodes = state.nodes.filter((node) => node.id !== nodeId);
    /** Purpose: Remove edges entering or leaving the deleted node. Why: Node deletion must not leave dangling connections. */
    const edges = state.edges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId);
    return {
      nodes,
      edges,
      selectedNodeId: state.selectedNodeId === nodeId ? '' : state.selectedNodeId,
    };
  }),

  // Purpose: append a graph connection. Why: new canvas connections must enter the shared serialized graph state.
  addEdge: (edge) => set((state) => ({
    edges: [...state.edges, edge],
  })),

  // Purpose: patch an existing connection and merge its branch metadata. Why: editing labels/conditions must not erase other edge data.
  updateEdge: (edgeId, patch) => set((state) => ({
    edges: state.edges.map((edge) => (
      edge.id === edgeId
        ? {
            ...edge,
            ...patch,
            data: {
              ...(edge.data || {}),
              ...(patch.data || {}),
            },
          }
        : edge
    )),
  })),

  // Purpose: remove a connection by ID. Why: edge deletion must be reflected in both the canvas and future API saves.
  removeEdge: (edgeId) => set((state) => ({
    edges: state.edges.filter((edge) => edge.id !== edgeId),
  })),

  // Purpose: record identifiers returned/confirmed after save. Why: subsequent updates must target the persisted flow in its bot/environment scope.
  setFlowAfterSave: (flowId, botId, environmentId) => set((state) => ({
    flowId,
    botId: botId || state.botId,
    environmentId: environmentId || state.environmentId,
  })),

  // Purpose: produce the current backend-compatible flow payload. Why: save callers should share the canonical editor-to-API serializer.
  getSavePayload: () => serializeFlowToApi(get()),
  // Purpose: return the currently selected graph node or null. Why: panels and commands need a safe lookup against live store state.
  getSelectedNode: () => get().nodes.find((node) => node.id === get().selectedNodeId) || null,
  // Purpose: move a node template to the front of the recent list. Why: the palette can make commonly used node types quicker to find.
  markNodeAsRecentlyUsed: (libraryKey) => set((state) => ({
    recentNodeKeys: libraryKey
      ? [libraryKey, ...state.recentNodeKeys.filter((key) => key !== libraryKey)].slice(0, 8)
      : state.recentNodeKeys,
  })),
}));

export default useFlowBuilderStore;
